import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { ArticleShowPage } from '../features/articles';
import {
  articleShowQuery,
  isNotFoundError,
  isSlugRedirect,
  sidebarQuery,
  type ArticleShowData,
  type ArticleShowResult,
} from '../lib/queries';
import {
  articleUrl,
  categoryUrl,
  clamp,
  isoDate,
  LOGO,
  ogImageFor,
  ogImageMeta,
  ORGANIZATION_ID,
  SITE_LOCALE,
  SITE_NAME,
  SITE_URL,
  stripHtml,
  type OgImage,
} from '../lib/seo';

type Article = ArticleShowData['article'];

function articleDescription(a: Article): string {
  const raw = a.metaDescription ?? a.ogDescription ?? a.excerpt ?? stripHtml(a.html);
  return clamp(raw, 160);
}

function articleJsonLd(
  a: Article,
  url: string,
  image: OgImage,
  tags: ReadonlyArray<{ name: string }>,
): string {
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': a.schemaType || 'NewsArticle',
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    url,
    headline: clamp(a.title, 110),
    description: articleDescription(a),
    image: [image.url],
    thumbnailUrl: image.url,
    // The editorial tags are the only place the specific entities in a piece
    // (a town, a club, a ministry) are stated in machine-readable form.
    keywords: tags.length ? tags.map((t) => t.name).join(', ') : undefined,
    datePublished: isoDate(a.publishedAt) ?? undefined,
    dateModified: isoDate(a.updatedAt) ?? isoDate(a.publishedAt) ?? undefined,
    inLanguage: 'ar',
    isAccessibleForFree: true,
    articleSection: a.category?.name ?? undefined,
    wordCount: a.wordCount ?? undefined,
    // Bylines are never surfaced on the site, so the organization is credited
    // as the author rather than the person who wrote the piece.
    author: { '@type': 'Organization', name: SITE_NAME, '@id': ORGANIZATION_ID },
    // Reference the organization declared once in __root rather than
    // redefining it — a second, thinner publisher node competes with it.
    publisher: {
      '@id': ORGANIZATION_ID,
      '@type': 'NewsMediaOrganization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: LOGO.url,
        width: LOGO.width,
        height: LOGO.height,
      },
    },
  });
}

function breadcrumbJsonLd(a: Article, url: string): string {
  const items: Array<Record<string, unknown>> = [
    { '@type': 'ListItem', position: 1, name: 'الرئيسية', item: SITE_URL },
  ];
  if (a.category) {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: a.category.name,
      item: categoryUrl(a.category.slug),
    });
  }
  items.push({ '@type': 'ListItem', position: items.length + 1, name: a.title, item: url });
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  });
}

export const Route = createFileRoute('/articles/$slug')({
  loader: async ({ context, params }) => {
    if (!params.slug) throw notFound();
    // Prefetch sidebar in parallel; article is required.
    void context.queryClient.prefetchQuery(sidebarQuery());
    // The return value matters: `head` reads it via `match.loaderData`. Awaiting
    // without returning leaves loaderData undefined, which silently drops every
    // article meta tag and both JSON-LD blocks from the SSR response.
    let data: ArticleShowResult;
    try {
      data = await context.queryClient.ensureQueryData(articleShowQuery(params.slug));
    } catch (err) {
      // A slug that does not exist is a 404, not a 500. Letting the API error
      // escape rendered the site shell with a 500 status and the site-wide
      // `index, follow` robots tag: Google keeps retrying the URL and never
      // learns the article is gone.
      if (isNotFoundError(err)) throw notFound();
      throw err;
    }
    // A retired slug still resolves, through the slug-history table, to the
    // article's current slug. The API answers 200 with `{ redirectTo }` and no
    // `article`, which crashed the page on `article.slug`. Answer a 301 instead:
    // the reader lands on the live URL and Google folds the old one into it.
    if (isSlugRedirect(data)) {
      throw redirect({
        to: '/articles/$slug',
        params: { slug: data.redirectTo },
        statusCode: 301,
      });
    }
    return data;
  },
  head: ({ params, match }) => {
    const data = (match.loaderData as ArticleShowData | undefined) ?? undefined;
    const article = data?.article;
    const url = articleUrl(params.slug);
    if (!article) {
      return {
        meta: [{ title: `${SITE_NAME} - Telesafi.ma` }],
        links: [{ rel: 'canonical', href: url }],
      };
    }
    const title = article.metaTitle ?? article.title;
    const fullTitle = `${title} - ${SITE_NAME}`;
    const description = articleDescription(article);
    const canonicalHref = article.canonicalUrl ?? url;
    const image = ogImageFor({
      ogImageMedia: article.ogImageMedia,
      coverMedia: article.coverMedia,
      alt: article.title,
    });
    const publishedIso = isoDate(article.publishedAt);
    const modifiedIso = isoDate(article.updatedAt) ?? publishedIso;
    return {
      meta: [
        { title: fullTitle },
        { name: 'description', content: description },
        // Per-article override (editors can set noindex on a given piece);
        // falls back to the site default declared in __root.
        ...(article.robots
          ? [{ name: 'robots', content: `${article.robots}, max-image-preview:large` }]
          : []),
        { property: 'og:title', content: article.ogTitle ?? title },
        { property: 'og:description', content: article.ogDescription ?? description },
        { property: 'og:url', content: canonicalHref },
        { property: 'og:type', content: 'article' },
        { property: 'og:locale', content: SITE_LOCALE },
        { property: 'og:site_name', content: SITE_NAME },
        ...ogImageMeta(image),
        ...(publishedIso
          ? [{ property: 'article:published_time', content: publishedIso }]
          : []),
        ...(modifiedIso ? [{ property: 'article:modified_time', content: modifiedIso }] : []),
        ...(article.category
          ? [{ property: 'article:section', content: article.category.name }]
          : []),
        // The tags are no longer rendered as links (there is no tag archive),
        // so `article:tag` is the only place aggregators and social scrapers
        // can read the entities a piece is about.
        ...(data?.tags ?? []).map((t) => ({ property: 'article:tag', content: t.name })),
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
      ],
      links: [{ rel: 'canonical', href: canonicalHref }],
      scripts: [
        {
          type: 'application/ld+json',
          children: articleJsonLd(article, canonicalHref, image, data?.tags ?? []),
        },
        { type: 'application/ld+json', children: breadcrumbJsonLd(article, canonicalHref) },
      ],
    };
  },
  component: ArticleShowPage,
});
